import { api_configuration } from "../../common";
import api_client_reply = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_reply.alan_api;
import api_client_request = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_request.alan_api;
import api_cs = api_configuration.types.client_state.alan_api;
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import * as mapToIdPathBuilder from "../state/mapToIdPathBuilder";
import { updateEntriesSelection } from "../state/selection";
import { getStaticDecimalsFromNumericalType, serializeNumber } from "./serializeNumber";
import {
	hasResult,
	subscribeToEntryStyle,
	subscribeToCellNumericalTypeProperties,
	subscribeToStategroupCellStyle,
	getTotalAmountOfLocalEntries,
	addResultEntry,
	removeResultEntry,
	getIndexedResultEntries,
	remoteQueryEntry,
	isSubscribed,
	getPropertyForCell,
} from "../state/Query";
import * as Cell from "../state/Cell";
import { client_state } from "../state/ClientState";
import { registerIdPath } from "../state/registerClientState";
import DestructorTree, { addChild } from "../DestructorTree";
import { getTranslation, subscribeToTranslation } from "../state/phrases";

function followConditionalChildPath(
	id_path: IdPathBuilder,
	conditional_child_path: api_client_request.Cconditional_child_path
): IdPathBuilder {
	const has_steps = conditional_child_path.properties.has_steps.state;
	switch (has_steps.name) {
		case "no":
			return id_path;
		case "yes": {
			const type = has_steps.node.properties.type.state;
			switch (type.name) {
				case "group":
					return followConditionalChildPath(
						id_path.group(type.node.properties.group.ref.parent.parent.key),
						has_steps.node.properties.tail
					);
				case "state":
					return followConditionalChildPath(
						id_path.state(type.node.properties.state_group.ref.parent.parent.key, type.node.properties.state.ref.key),
						has_steps.node.properties.tail
					);
			}
		}
	}
}

function walkCollectionQueryEntries_impl(
	entries_type: api_client_reply.Ccollection_query_entries,
	path_builder: IdPathBuilder,
	entry_name: string,
	on_entry: (
		name: string,
		id_path_builder: IdPathBuilder,
		type: api_client_reply.Cno__has_steps__collection_query_entries
	) => void
) {
	switch (entries_type.properties.has_steps.state.name) {
		case "no": {
			on_entry(entry_name, path_builder, entries_type.properties.has_steps.state.node);
			break;
		}
		case "yes": {
			const $ = entries_type.properties.has_steps.state.node;
			const collection_path = entries_type.input.collection_path().properties.has_steps.cast("yes");
			const new_path_builder = followConditionalChildPath(path_builder, collection_path.properties.head);
			for (const [key, entry] of $.properties.entries) {
				let entry_id_path = new_path_builder.collectionEntry(collection_path.properties.collection.entry, key);
				switch (entry.properties.type.state.name) {
					case "candidate": {
						const $ = entry.properties.type.state.node;
						switch ($.properties.reference_result.state.name) {
							case "yes": {
								entry_id_path = mapToIdPathBuilder.fromClientReply(
									$.properties.reference_result.state.node.properties.id_path
								);
								break;
							}
							case "no":
								break;
							default:
								unexpectedState($.properties.reference_result.state);
						}
						break;
					}
					case "existing entry":
						break;
					default:
						unexpectedState(entry.properties.type.state);
				}
				walkCollectionQueryEntries_impl(
					entry.properties.tail,
					entry_id_path,
					entry_name.length > 0 ? entry_name + "." + key : key,
					on_entry
				);
			}
			break;
		}
		default:
			unexpectedState(entries_type.properties.has_steps.state);
	}
}
function walkCollectionQueryEntries(
	collection_reply: api_client_reply.Cexists__context__collection_query,
	on_entry: (
		name: string,
		id_path_builder: IdPathBuilder,
		type: api_client_reply.Cno__has_steps__collection_query_entries
	) => void
) {
	const entries = collection_reply.properties.entries.state;
	switch (entries.name) {
		case "yes":
			walkCollectionQueryEntries_impl(
				entries.node.properties.entries,
				mapToIdPathBuilder.fromClientRequest(collection_reply.parent.inferences.source().properties.context_node_path),
				"",
				on_entry
			);
			break;
		case "no":
	}
}

function createIdPathBuilderFromClientReplyIdPath(id_path: api_client_reply.Cid_path__client_reply): IdPathBuilder {
	let builder = IdPathBuilder.create();
	while (id_path.properties.has_steps.state.name === "yes") {
		const $ = id_path.properties.has_steps.state.node;
		id_path = $.properties.tail;
		switch ($.properties.type.state.name) {
			case "collection entry": {
				const ce = $.properties.type.state.node;
				builder = builder.collectionEntry(ce.properties.collection.entry, ce.properties.id);
				break;
			}
			case "group": {
				const g = $.properties.type.state.node;
				builder = builder.group(g.properties.group.entry);
				break;
			}
			case "state": {
				const s = $.properties.type.state.node;
				builder = builder.state(s.properties.state_group.entry, s.properties.state.entry);
				break;
			}
		}
	}
	return builder;
}

function getIdPathFromQueryResult(
	source: api_client_reply.Cno__has_steps__collection_query_entries,
	column_name: string
) {
	const id_path_column = source.properties.properties.properties.properties.get(column_name + "idpath");
	if (
		id_path_column &&
		id_path_column.properties.result.state.name === "exists" &&
		id_path_column.properties.result.state.node.properties.type.state.name === "id path"
	) {
		return createIdPathBuilderFromClientReplyIdPath(
			id_path_column.properties.result.state.node.properties.type.state.node.properties.id_path
		);
	}
	return undefined;
}

function subscribeToEntryExpressions(
	target: api_cs.Centries__yes__has_result,
	source: api_client_reply.Cno__has_steps__collection_query_entries
) {
	const destructor = addChild(target);

	subscribeToEntryStyle(target, destructor);

	target.properties.cells.forEach((cell) => {
		const cell_name = cell.key.entry;
		switch (cell.properties.type.state.name) {
			case "number": {
				const number_cell = cell.properties.type.state.node;
				const number_column = number_cell.inferences.column_type();
				const app_number = number_column.inferences.number_type().properties.number.ref.inferences.number();
				switch (app_number.properties.ui.properties.dynamic_numerical_type.state.name) {
					case "no": {
						const this_node = client_state.properties.root;
						subscribeToCellNumericalTypeProperties(number_cell, this_node, destructor);
						break;
					}
					case "yes": {
						const id_path = getIdPathFromQueryResult(source, cell_name);
						if (id_path) {
							subscribeToCellNumericalTypeProperties(number_cell, registerIdPath(id_path, destructor), destructor);
						}
						break;
					}
					default:
						unexpectedState(app_number.properties.ui.properties.dynamic_numerical_type.state);
				}
				break;
			}
			case "file":
				break;
			case "text":
				break;
			case "widget":
				break;
			case "state group": {
				const state_group_cell = cell.properties.type.state.node;
				const id_path = getIdPathFromQueryResult(source, cell_name);
				if (id_path) {
					subscribeToStategroupCellStyle(state_group_cell, id_path, destructor);
				}
				break;
			}
		}
	});
}

function updateEntryFromQueryResult(
	target: api_cs.Centries__yes__has_result,
	source: api_client_reply.Cno__has_steps__collection_query_entries,
	entry?: api_cs.Centries__collection
) {
	for (const [key, target_cell] of target.properties.cells) {
		const source_property = source.properties.properties.properties.properties.get(key);
		if (source_property === undefined) {
			continue;
		}
		switch (source_property.properties.result.state.name) {
			case "does not exist":
				Cell.unset(target_cell);
				break;
			case "permission denied":
				Cell.unset(target_cell);
				break;
			case "exists": {
				const exists = source_property.properties.result.state.node;
				switch (target_cell.properties.type.state.name) {
					case "text": {
						const target_type = target_cell.properties.type.state.node;
						if (entry && getPropertyForCell(entry, target_type)) break;
						const source_type = exists.properties.type.cast("text");
						const source_text = source_type.properties.value;
						Cell.updateText(target_type, source_text);
						break;
					}
					case "file": {
						const target_type = target_cell.properties.type.state.node;
						if (entry && getPropertyForCell(entry, target_type)) break;
						const source_type = exists.properties.type.cast("file");
						const source_token = source_type.properties.token;
						const source_extension = source_type.properties.extension;
						Cell.updateFile(target_type, source_token, source_extension);
						break;
					}
					case "number": {
						const target_type = target_cell.properties.type.state.node;
						if (entry && getPropertyForCell(entry, target_type)) break;
						const source_type = exists.properties.type.cast("number");
						const source_number = source_type.properties.value.value;
						const source_textual_representation = source_type.inferences
							.number()
							.properties.expression.properties.type.switch({
								"number property": (number_property_expresion) => {
									const numerical_type =
										number_property_expresion.properties.signed_number_property.properties.property_name.ref.properties
											.numerical_type.ref;
									return serializeNumber(
										source_type.properties.value.value,
										getStaticDecimalsFromNumericalType(numerical_type),
										numerical_type
									);
								},
								count: () => String(source_type.properties.value.value),
								division: () => String(source_type.properties.value.value),
								maximum: () => String(source_type.properties.value.value),
								minimum: () => String(source_type.properties.value.value),
								product: () => String(source_type.properties.value.value),
								remainder: () => String(source_type.properties.value.value),
								"state based": () => String(source_type.properties.value.value),
								sum: () => String(source_type.properties.value.value),
								"sum list": () => String(source_type.properties.value.value),
								value: () => String(source_type.properties.value.value),
							});
						Cell.updateNumber(target_type, source_number, source_textual_representation);
						break;
					}
					case "state group": {
						const target_type = target_cell.properties.type.state.node;
						if (entry && getPropertyForCell(entry, target_type)) break;
						const source_type = exists.properties.type.cast("state group");
						const source_state = source_type.properties.state.entry;
						Cell.updateStategroup(target_type, getTranslation(source_state));
						addCellToTranslation(target_type, source_state);
						break;
					}
					case "widget":
						break;
				}
				break;
			}
		}
	}
}

let translation_destructor: DestructorTree | undefined;
const states_to_translate = new Map<api_cs.Cstate_group__type__cells, string>();

function addCellToTranslation(cell: api_cs.Cstate_group__type__cells, state_name: string) {
	states_to_translate.set(cell, state_name);
	cell.onDestroy(() => {
		states_to_translate.delete(cell);
	});
	if (translation_destructor === undefined) {
		translation_destructor = new DestructorTree();
		subscribeToTranslation(
			"",
			translation_destructor,
			() => {
				for (const [cell, state_name] of states_to_translate) {
					Cell.updateStategroup(cell, getTranslation(state_name));
				}
			},
			false
		);
	}
}

function updateTotalAmountOfEntries(
	result: api_client_reply.Cexists__context__collection_query,
	query: api_cs.Cquery__client_state,
	total_amount_of_entries: number
) {
	query.properties.has_result.switch({
		no: undefined,
		yes: ($) => {
			$.set_total_amount_of_entries(total_amount_of_entries);
			result.properties.entries.switch<void>({
				no: () => $.properties.has_more_entries_than_maximum.set("no", {}),
				yes: (has_entries) =>
					has_entries.properties.limit_exceeded.switch<void>({
						no: () => $.properties.has_more_entries_than_maximum.set("no", {}),
						yes: () => {
							$.properties.has_more_entries_than_maximum.set("yes", {
								"can show all":
									result.properties.amount_of_entries.value >
									query.properties.definition.ref.properties.query_limits.properties.absolute_maximum.value
										? ["no", {}]
										: ["yes", {}],
							});
						},
					}),
			});
		},
	});
}

export default function (
	result: api_client_reply.Cexists__context__collection_query,
	query: api_cs.Cquery__client_state
): void {
	const has_result = hasResult(query);
	const total_amount_of_entries = result.properties.amount_of_entries.value + getTotalAmountOfLocalEntries(query);
	updateTotalAmountOfEntries(result, query, total_amount_of_entries);

	const keys = new Set<string>();
	let entries_indexed_on_key = getIndexedResultEntries(has_result);
	walkCollectionQueryEntries(result, (name, id_path, result_entry) => {
		let entry: api_cs.Centries__yes__has_result;
		if (entries_indexed_on_key.has(name)) {
			entry = entries_indexed_on_key.get(name)!;
		} else {
			entry = addResultEntry(has_result, remoteQueryEntry(id_path));
			subscribeToEntryExpressions(entry, result_entry);
		}
		if (isSubscribed(query)) {
			const initialized = entry.properties.entry_status.properties.initialized.state;
			switch (initialized.name) {
				case "no":
					updateEntryFromQueryResult(entry, result_entry);
					break;
				case "yes":
					updateEntryFromQueryResult(
						entry,
						result_entry,
						initialized.node.properties.referenced_node.output.result_node().input.entity().cast("entry")
					);
					break;
				case "unknown":
					break;
			}
		} else {
			updateEntryFromQueryResult(entry, result_entry);
		}
		keys.add(name);
	});

	// Cleanup entries that are not in the query result anymore
	entries_indexed_on_key = getIndexedResultEntries(has_result);
	for (const [k, entry] of entries_indexed_on_key) {
		if (!keys.has(k)) {
			switch (entry.properties.entry_status.properties.initialized.state.name) {
				case "unknown":
					break;
				case "yes": {
					const initialized = entry.properties.entry_status.properties.initialized.state.node;
					const referenced_node = initialized.properties.referenced_node.output.result_node();
					switch (referenced_node.properties.location.state.name) {
						case "local":
							break;
						case "remote": {
							removeResultEntry(entry);
							break;
						}
						default:
							unexpectedState(referenced_node.properties.location.state);
					}
					break;
				}
				case "no":
					removeResultEntry(entry);
					break;
				default:
					unexpectedState(entry.properties.entry_status.properties.initialized.state);
			}
		}
	}

	updateEntriesSelection(query);
}
